import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import formatNumber from "@/utils/formatNumber";
import dummyDonutChartData from "@/utils/dummyDonutChartData.json";
import { orderFundingTypesInObject } from "@/utils/fundingTypesOrders";
import useWindowSize from "@/hooks/useWindowSize";
import Image from "next/image";
import shh from "@/assets/icons/shh.svg";
import LucidMascot from "@/assets/images/AishaMoonwalk.gif";

// Define a consistent mapping of funding types to colors
const fundingTypeColors = {
  "Pre-Seed": "#FF1493", // Deep pink
  Seed: "#32CD32", // Lime green
  "Pre-Seed to Seed": "#FFD700", // Gold
  "Pre-Series A": "#1E90FF", // Dodger blue
  "Series A": "#FF4500", // Orange red
  "Series B": "#8A2BE2", // Blue violet
  "Series C": "#00FFFF", // Cyan
  "Series D": "#FF00FF", // Magenta
  "Series E": "#ADFF2F", // Green yellow
  "Series F": "#FF8C00", // Dark orange
  "Series G": "#008B8B", // Dark cyan
  "Series H": "#BA55D3", // Medium orchid
  "Series I": "#DC143C", // Crimson
  "Series J": "#DAA520", // Goldenrod
  "Late Stage": "#800000", // Maroon
  "PE Round": "#00FA9A", // Medium spring green
  "Pre-IPO": "#9ACD32", // Yellow green
  ICO: "#FF69B4", // Hot pink
  Grant: "#191970", // Midnight blue
  "Debt Financing": "#A9A9A9", // Dark gray
  Others: "#778899", // Light slate gray
};

// Fallback colors for any new funding types not in the mapping
const fallbackColors = [
  "#FF1493", // Deep pink
  "#32CD32", // Lime green
  "#FFD700", // Gold
  "#1E90FF", // Dodger blue
  "#FF4500", // Orange red
  "#8A2BE2", // Blue violet
  "#00FFFF", // Cyan
  "#FF00FF", // Magenta
  "#ADFF2F", // Green yellow
  "#FF8C00", // Dark orange
  "#008B8B", // Dark cyan
  "#BA55D3", // Medium orchid
  "#DC143C", // Crimson
  "#DAA520", // Goldenrod
  "#800000", // Maroon
  "#00FA9A", // Medium spring green
  "#9ACD32", // Yellow green
  "#FF69B4", // Hot pink
  "#191970", // Midnight blue
  "#A9A9A9", // Dark gray
];

export default function FundingChart({
  data: _data,
  fundraised: _fundraised,
  isLoading = false,
  lightMode = false,
  width,
  height,
  legendPosition = "right",
}) {
  const [chartOptions, setChartOptions] = useState(null);
  const windowSize = useWindowSize();
  const setChartTheme = () => {
    // Setting up the theme
    Highcharts.theme = {
      colors: ["#FDD089", "#FF7F79", "#A0446E", "#251535"],
      chart: {
        backgroundColor: "transparent",
      },
      title: {
        style: {
          color: "#A0A0A3",
        },
      },
      tooltip: {
        backgroundColor: "#fff",
        style: {
          color: "#000",
          zIndex: 9999,
        },
        pointFormat:
          "<div><span>{series.name}</span>: <b>{point.displayAmount}</b></div>",
      },
      plotOptions: {
        series: {
          dataLabels: {
            color: "#F0F0F3",
          },
        },
      },
    };

    // Applying the theme
    Highcharts.setOptions(Highcharts.theme);
  };

  let data = [];
  let fundraised = 0;

  // Only show dummy data if not loading and no real data available
  if (_data.length > 0) {
    data = _data;
    fundraised = _fundraised;
  } else if (!isLoading) {
    data = dummyDonutChartData;
    fundraised = 5000000000;
  }

  const label = orderFundingTypesInObject(data).map((item) => {
    // Get color from the mapping or use a fallback if not found
    const color =
      fundingTypeColors[item.name] ||
      fallbackColors[Math.floor(Math.random() * fallbackColors.length)];

    return {
      name: item.name,
      amount: item.total_amount,
      color: color,
    };
  });

  useEffect(() => {
    if (typeof window !== "undefined" && !isLoading && data.length > 0) {
      const options = {
        chart: {
          type: "pie",
          width: width ?? (windowSize.width > 1024 ? 300 : 260),
          height: height ?? (windowSize.width > 1024 ? 300 : 260),
          backgroundColor: "transparent",
        },
        title: {
          text: "Funding Chart",
          style: {
            display: "none",
          },
        },
        tooltip: {
          outside: true,
          useHTML: true,
          style: {
            zIndex: 9999,
          },
        },
        plotOptions: {
          pie: {
            innerSize: "90%",
            borderWidth: 0,
            dataLabels: {
              enabled: false,
            },
            minSize: 10,
          },
        },
        series: [
          {
            name: "Round Raised in USD",
            data: label.map((item) => ({
              name: item.name,
              y: item.amount,
              displayAmount: formatNumber(item.amount),
              color: item.color,
            })),
          },
        ],
      };

      setChartOptions(options);

      setChartTheme();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_data, _fundraised, windowSize.width, isLoading, width, height]);

  // Loading state
  if (isLoading) {
    return (
      <div className='flex items-center justify-center lg:space-x-6'>
        <div className='relative -ml-8 -mr-1 lg:mr-0 lg:ml-0'>
          <div
            className='flex items-center justify-center'
            style={{
              width: windowSize.width > 1024 ? 300 : 260,
              height: windowSize.width > 1024 ? 300 : 260,
            }}
          >
            <div className='relative'>
              {/* Custom loading animation */}
              <Image
                src={LucidMascot.src}
                alt='Loading...'
                width={windowSize.width > 1024 ? 120 : 100}
                height={windowSize.width > 1024 ? 120 : 100}
                className='object-contain'
                unoptimized={true}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex justify-center ${legendPosition === "bottom" ? "flex-col items-center" : "flex-row items-center"}`}
    >
      <div
        className={`relative ${
          fundraised === 0 ? "w-full" : "-ml-8 -mr-1 lg:mr-0 lg:ml-0"
        }`}
      >
        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        <div
          className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center w-full pointer-events-none ${lightMode ? "z-10" : "lg:z-[-1]"}`}
        >
          <p
            className={`text-sm font-semibold leading-none ${lightMode ? "text-gray-800" : "text-white"}`}
          >
            <span
              className={`mb-2 lg:mb-3 ${
                fundraised === 0 ? "hidden lg:block" : "block"
              }`}
            >
              Total Fundraised
            </span>
            <span
              className={`inline-block text-[40px] font-bold tracking-tight lg:text-5xl ${lightMode ? "text-black" : "text-transparent bg-clip-text bg-gradient-to-tr from-yellow-1 to-red-1"} `}
            >
              {fundraised > 0 ? `$${formatNumber(fundraised)}` : "N/A"}
            </span>
          </p>
          {fundraised === 0 && (
            <>
              <div className='relative mx-auto mt-5 lg:mt-6 size-14'>
                <Image
                  src={shh.src}
                  className='absolute'
                  layout='fill'
                  alt='Round Image'
                />
              </div>
              <p className='mt-4 text-sm lg:text-xs text-white/30'>
                This startup has raised funding, but has not disclosed the
                amount of funds it has raised. AKA. secretive startup!
              </p>
            </>
          )}
        </div>
      </div>
      {fundraised > 0 && (
        <div
          className={`text-xs lg:text-[10px] ${lightMode ? "text-gray-700" : ""} ${legendPosition === "bottom" ? "flex flex-wrap justify-center gap-x-3 gap-y-2 -mt-2" : "space-y-2.5 lg:space-y-2"}`}
        >
          {label
            .filter((v) => v.amount)
            .map((item, index) => (
              <div className='flex items-center space-x-1.5' key={index}>
                <span
                  className='size-2.5 shrink-0 rounded-full'
                  style={{ backgroundColor: item.color }}
                ></span>
                <span className='leading-snug'>{item.name}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
